/**
 * Whether to remind a manager to finish setting up their workspace, and
 * how to stop reminding them.
 *
 * One state behind every reminder (the OnboardingNudge banner, the
 * top-bar Setup icon, its phone menu entry and the admin menu item) and
 * behind the welcome page's "Don't remind me again" / "Turn back on":
 * each used to read the dismissal on its own, so dismissing the banner
 * left the icon up until the next navigation re-read localStorage.
 * A change now notifies every consumer at once.
 */
import * as React from "react";

import { useMe } from "@/api/hooks";

import { useSetupChecklist } from "./useSetupChecklist";

const DISMISS_EVENT = "rw:setup-nudge-dismissed";
const dismissKey = (slug: string): string => `rw.onboarding.dismissed.${slug}`;

/** Choices this page could not save. Storage can be blocked (private
 *  mode, cleared site data) or full; the choice must still hold until a
 *  reload, for every consumer. Only then: while storage works it is the
 *  one truth, so turning the reminder back on is never overridden by a
 *  dismissal remembered here. */
const unsavedChoices = new Map<string, boolean>();

function readDismissed(slug: string | null): boolean {
  if (!slug) return false;
  const unsaved = unsavedChoices.get(slug);
  if (unsaved !== undefined) return unsaved;
  try {
    return localStorage.getItem(dismissKey(slug)) === "1";
  } catch {
    return false;
  }
}

/** Same-tab changes fire our event; another tab's fire ``storage``. */
function subscribe(onChange: () => void): () => void {
  window.addEventListener(DISMISS_EVENT, onChange);
  window.addEventListener("storage", onChange);
  return () => {
    window.removeEventListener(DISMISS_EVENT, onChange);
    window.removeEventListener("storage", onChange);
  };
}

/** Stop, or resume, reminding about ``slug``'s setup. Every reminder
 *  follows at once. */
export function setSetupNudgeDismissed(slug: string, dismissed: boolean): void {
  try {
    if (dismissed) localStorage.setItem(dismissKey(slug), "1");
    else localStorage.removeItem(dismissKey(slug));
    unsavedChoices.delete(slug);
  } catch {
    unsavedChoices.set(slug, dismissed);
  }
  window.dispatchEvent(new Event(DISMISS_EVENT));
}

/** Whether the setup reminder for ``slug`` is dismissed, kept current. */
export function useSetupNudgeDismissed(slug: string | null): boolean {
  return React.useSyncExternalStore(
    subscribe,
    () => readDismissed(slug),
    () => false,
  );
}

export type SetupNudge = {
  visible: boolean;
  /** The org the checklist is for (null when none). */
  slug: string | null;
  incompleteCount: number;
  /** Stop reminding about this org, everywhere, from now on. */
  dismiss: () => void;
};

export function useSetupNudge(): SetupNudge {
  const me = useMe();
  const { slug, incompleteCount, ready } = useSetupChecklist();
  const dismissed = useSetupNudgeDismissed(slug);
  const dismiss = React.useCallback(() => {
    if (slug) setSetupNudgeDismissed(slug, true);
  }, [slug]);

  // Only org admins/managers can action the setup steps (add a code
  // source, brand, etc.), so plain members are never nagged.
  const canManage = Boolean(me.data?.admin_scope?.can_access_admin);
  const visible = canManage && Boolean(slug) && ready && incompleteCount > 0 && !dismissed;
  return { visible, slug, incompleteCount, dismiss };
}
